import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { debounce } from 'throttle-debounce';

import { ModalStore } from 'ui/base/modal';
import { BackButton } from 'ui/base/button/back_button';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { useOnScreen } from 'ui/base/screen';
import { ToastID, defaultToast } from 'ui/base/toast';
import { FixedStandardPage } from 'ui/base/page/standard_page/standard_page';

import { ResultKind, ServiceStatus } from 'services/base/types';
import { BasePathItems, BasePath, pagePathToPathString } from 'services/base/page_path';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { togglePullToRefresh } from 'services/base/ipc';

import { Pangolin, PlanFirewallPackage } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { authorizePlanFirewall } from 'services/accounts/logic/plans/plan_firewall';

import { IPangolinDomain } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { HistoryService } from 'services/pangolin/history_service';

import { Redirect } from 'pages/core/redirect/redirect';

import { HistoryItem, HistoryProgress } from './ui/history_item/history_item';

import { FilterHistoryModal } from './modals/filter_history/filter_history';
import { ViewHistoryModal } from './modals/view_history/view_history';

import Messages from './list_history_messages';
import baseStyles from './list_history.module.css';

type ListHistoryStyles = {
  header: string;
  headerItem: string;
  headerText: string;
  headerButton: string;
  headerButtonIcon: string;
  content: string;
  footer: string;
  items: string;
};

const themedStyles: ThemedStyles<ListHistoryStyles> = {
  [Theme.DAYLIGHT]: {
    header: baseStyles.header,
    headerItem: baseStyles.headerItem,
    headerText: baseStyles.daylightHeaderText,
    headerButton: baseStyles.headerButton,
    headerButtonIcon: baseStyles.daylightHeaderButtonIcon,
    content: baseStyles.content,
    footer: baseStyles.footer,
    items: baseStyles.items,
  },
  [Theme.MIDNIGHT]: {
    header: baseStyles.header,
    headerItem: baseStyles.headerItem,
    headerText: baseStyles.midnightHeaderText,
    headerButton: baseStyles.headerButton,
    headerButtonIcon: baseStyles.midnightHeaderButtonIcon,
    content: baseStyles.content,
    footer: baseStyles.footer,
    items: baseStyles.items,
  },
};

const doDebouncedFetchPage = debounce(1000, (domain: IPangolinDomain, pangolin: Pangolin) => {
  HistoryService.fetchPage(domain, pangolin).then(result => {
    if (result.kind !== ResultKind.OK) {
      if (!pangolin.onlineStatus) {
        defaultToast(ToastID.ERROR_QUERY_PANGOLIN_OFFLINE);
      } else if (result.status === 402) {
        defaultToast(ToastID.ERROR_QUERY_PLAN_REQUIRED);
      } else {
        defaultToast(ToastID.ERROR_QUERY_GENERIC);
      }
    }
  });
});

const doDebouncedSetValue = debounce(50, (setValue, value) => {
  setValue(value);
});

type HistoryLoaderProps = {
  domain: IPangolinDomain;
  pangolin: Pangolin;
};
const HistoryLoader = observer(({ domain, pangolin }: HistoryLoaderProps) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const online = pangolin.onlineStatus;
  const capped = domain.historyCapped;
  const status = domain.historyStatus;
  const failed = status === ServiceStatus.FAILED;
  const loading = status === ServiceStatus.LOADING;

  const visible = useOnScreen(ref);
  const empty = domain.history.size === 0;

  const trigger = visible || empty;

  const fetch = online && !capped && !failed && !loading && trigger;
  React.useEffect(() => {
    if (fetch) doDebouncedFetchPage(domain, pangolin);
  }, [fetch, domain, pangolin]);

  return <div ref={ref} />;
});

type HistoryItemsContainerProps = {
  domain: IPangolinDomain;
  pangolin: Pangolin;
};
const HistoryItemsContainer = observer(({ domain, pangolin }: HistoryItemsContainerProps) => {
  const styles = useStyles(themedStyles);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const capped = domain.historyCapped;
  const status = domain.historyStatus;
  const failed = status === ServiceStatus.FAILED;
  const loading = status === ServiceStatus.LOADING;

  React.useEffect(() => {
    if (!capped && !failed) {
      document.getAnimations().forEach((animation) => {
        animation.cancel();
        animation.play();
      });
    }
  }, [capped, failed]);

  return (
    <div className={styles.items}>
      {
        domain.history.map((item, id) =>
          <HistoryItem
            key={`item-${id}`}
            domain={domain}
            pangolin={pangolin}
            pair={[id, item]}
            locale={locale}
            format={format}
          />
        )
      }
      {
        !loading ?
          <HistoryLoader
            domain={domain}
            pangolin={pangolin}
          /> : null
      }
      {
        !capped && !failed ? <>
          <HistoryProgress
            locale={locale}
            format={format}
          />
          <HistoryProgress
            locale={locale}
            format={format}
          />
        </> : null
      }
    </div>
  );
});

type BaseListHistoryProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
export const BaseListHistory = observer(({ pangolin, domain }: BaseListHistoryProps) => {
  const nav = useHistory();
  const styles = useStyles(themedStyles);

  const filterItemsMatch = useRouteMatch(pagePathToPathString({
    kind: BasePath.HISTORY_LIST,
    pangolinID: pangolin.id,
    itemsPath: {
      kind: BasePathItems.FILTER_ITEMS,
    },
  }));

  const viewItemMatch = useRouteMatch<{itemID: string}>(
    pagePathToPathString({
      kind: BasePath.HISTORY_LIST,
      pangolinID: pangolin.id,
      itemsPath: {
        kind: BasePathItems.VIEW_ITEM,
        itemID: ':itemID',
      },
    })
  );

  const onShowFilterModal = React.useCallback(action(() => {
    if (authorizePlanFirewall(pangolin, PlanFirewallPackage.BASIC)) {
      nav.push(pagePathToPathString({
        kind: BasePath.HISTORY_LIST,
        pangolinID: pangolin.id,
        itemsPath: {
          kind: BasePathItems.FILTER_ITEMS,
        },
      }));
    } else {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    }
  }), []);

  React.useEffect(() => {
    if (domain.historyFiltered) {
      domain.historyFiltered = false;
      HistoryService.clearData(domain);
    } else {
      HistoryService.clearAll(domain);
    }
    return () => {
      HistoryService.clearAll(domain);
    };
  }, [domain]);

  const [filterModalStore] = React.useState(new ModalStore(false));
  const [viewModalStore] = React.useState(new ModalStore(false));

  const [isScrollOnTop, setScrollOnTop] = React.useState(true);
  const handleScroll = React.useCallback((event: React.UIEvent<HTMLElement>) => {
    const scrollOnTop = event.currentTarget.scrollTop === 0;
    doDebouncedSetValue(setScrollOnTop, scrollOnTop);
  }, []);

  React.useEffect(() => {
    const isModalHidden = !filterModalStore.visible && !viewModalStore.visible;
    if (!isScrollOnTop || !isModalHidden) togglePullToRefresh(false);
    else togglePullToRefresh(true, () => { HistoryService.clearDate(domain); });
    return () => { togglePullToRefresh(false); };
  }, [domain, isScrollOnTop, filterModalStore.visible, viewModalStore.visible]);

  React.useEffect(() => {
    if (filterItemsMatch) filterModalStore.showModal();
    else filterModalStore.hideModal();
  }, [filterItemsMatch, filterModalStore]);

  React.useEffect(() => {
    if (viewItemMatch) {
      const { itemID } = viewItemMatch.params;

      const view = itemID && domain.historyItem;
      if (view) viewModalStore.showModal();
      else viewModalStore.hideModal();

      const gone = itemID && !domain.historyItem;
      if (gone) {
        nav.replace(pagePathToPathString({
          kind: BasePath.HISTORY_LIST,
          pangolinID: pangolin.id,
        }));
      }
    } else {
      viewModalStore.hideModal();
    }
  }, [viewItemMatch, viewModalStore, pangolin.id, domain.historyItem, nav]);

  return (
    <>
      <FilterHistoryModal
        domain={domain}
        modal={filterModalStore}
      />
      <ViewHistoryModal
        domain={domain}
        pangolin={pangolin}
        modal={viewModalStore}
      />
      <FixedStandardPage>
        <div className={styles.header}>
          <div className={styles.headerItem}>
            <BackButton/>
            <div className={styles.headerText}>
              {Messages.title()}
            </div>
          </div>
          <div className={styles.headerItem}>
            <button type='button' className={styles.headerButton}
                    onClick={onShowFilterModal}>
              <div className={styles.headerButtonIcon}/>
            </button>
          </div>
        </div>
        <div className={styles.content} onScroll={handleScroll}>
          {
            pangolin.onlineStatus ?
              <HistoryItemsContainer
                domain={domain}
                pangolin={pangolin}
              /> : null
          }
        </div>
        <div className={styles.footer}>
          <p>{Messages.creditIP2Location()}</p>
        </div>
      </FixedStandardPage>
    </>
  );
});

export const ListHistory = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  return (
    <BaseListHistory
      pangolin={pangolin}
      domain={domain}
    />
  );
});
